// w38 Marigold — variant that renders the standalone HTML at
// /templates/marigold.html (mobile-first autumn-marigold watercolor
// wedding invitation: cream + terracotta + sage, Caprasimo + Figtree,
// splash medallion, arch hero with shimmer names, garland divider,
// ornamental-frame invitation, four-event timeline, venue with map,
// "good to know" card, wishes wall, circular countdown, falling petals,
// in-iframe UZ/EN/RU switcher + music) inside a full-bleed iframe.
//
// The HTML's customizer accepts DD.MM.YYYY (or YYYY-MM-DD) directly —
// names/date/time/venue/address pass through raw on the query string.
import type { VariantProps } from "./types";
import { getLocale } from "@/lib/i18n";

// Flat .html — NOT a nested index.html. Go's http.FileServer 301-redirects
// any request ending in /index.html to the bare directory, which then falls
// through to the SPA and renders the wrong template.
const TEMPLATE_HTML = "/templates/marigold.html";

export function MarigoldStaticVariant({ t, ctx }: VariantProps) {
  const params = new URLSearchParams();
  if (t.names) params.set("names", t.names);
  if (t.date) params.set("date", t.date);
  if (t.time) params.set("time", t.time);
  if (t.venue) params.set("venue", t.venue);
  if (t.address) params.set("address", t.address);
  // ?map_url= drives the "Xaritada ochish" button; when absent the button
  // falls back to a maps search on the address / venue.
  if (t.mapUrl) params.set("map_url", t.mapUrl);

  // Scalar custom fields. Forwarded even when blank (typeof === "string")
  // so the customizer can tell "owner cleared it" (hide the slot) from
  // "never edited" (undefined → keep the template's default copy).
  const scalarKeys = [
    "bride_parents",
    "groom_parents",
    "venue_note",
    "info_dress",
    "info_stay",
    "info_gifts",
    "info_rsvp",
    "hashtag",
    "footer_note",
    "default_lang",
  ] as const;
  for (const k of scalarKeys) {
    const v = t.custom?.[k];
    if (typeof v === "string") params.set(k, v);
  }

  // Events timeline rides as a JSON array of {time,place,title,description}.
  // Forwarded only when the owner has touched the field (array present,
  // even empty) so the customizer can tell "owner cleared it" (hide the
  // section) from "never edited" (keep the four default events).
  const events = Array.isArray(t.custom?.events) ? t.custom.events : null;
  if (events !== null) params.set("events", JSON.stringify(events));

  if (ctx.slug) params.set("slug", ctx.slug);
  // ?audio=/music/<id>.mp3 — owner-picked soundtrack from the curated
  // MusicSelector library. Empty/unset = silent card.
  if (t.musicTrackId) params.set("audio", "/music/" + t.musicTrackId + ".mp3");

  // Demo/catalog preview (no real invitation slug): default the template's
  // in-iframe language to the app locale, so taklifla.com previews render in
  // English (and a RU viewer sees Russian). The HTMLs read ?lang= (all) and
  // ?default_lang= (newer premium ones) — set both. Owner-published
  // invitations (ctx.slug set) keep their own default_lang / built-in default.
  if (!ctx.slug) {
    const _loc = getLocale();
    if (!params.has("lang")) params.set("lang", _loc);
    if (!params.has("default_lang")) params.set("default_lang", _loc);
  }
  const src = `${TEMPLATE_HTML}?${params.toString()}`;

  return (
    <iframe
      src={src}
      title={t.names || "Marigold"}
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100svh",
        border: "none",
        display: "block",
        background: "#f5ead8",
      }}
    />
  );
}
